import { forwardRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperType } from "swiper";
import { Camera } from "lucide-react";
import useProductDetail from "@/app/lib/features/productDetail/productDetail.hook";
import FallbackImage from "@/app/components/elements/FallbackImage";
import SwiperButton from "@/app/components/elements/buttons/SwiperButton";
import ImageBadges from "./ImageBadges";
import ImageOverlays from "./ImageOverlays";

interface MainImageContainerProps {
  images: string[];
  onImageClick: () => void;
  onSlideChange: (swiper: SwiperType) => void;
  onSwiperInit: (swiper: SwiperType) => void;
  isCustomerImage: (index: number) => boolean;
}

const MainImageContainer = forwardRef<HTMLDivElement, MainImageContainerProps>(
  (
    { images, onImageClick, onSlideChange, onSwiperInit, isCustomerImage },
    ref,
  ) => {
    const { product } = useProductDetail();

    if (!product) return null;

    return (
      <div ref={ref} className="flex-1 min-w-0">
        <div className="relative w-full aspect-2/3 rounded-xl border overflow-hidden bg-white">
          <Swiper
            className="absolute inset-0 w-full h-full"
            onSwiper={onSwiperInit}
            onSlideChange={onSlideChange}
          >
            {images.map((image, i) => (
              <SwiperSlide
                key={i}
                className="relative w-full h-full cursor-zoom-in"
                onClick={onImageClick}
              >
                <FallbackImage
                  src={image}
                  alt={product.internet_product_description}
                  fill
                  className="object-cover"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 55vw, 45vw"
                  quality={90}
                  priority={i === 0}
                  loading={i === 0 ? undefined : "lazy"}
                />
                {isCustomerImage(i) && (
                  <div className="absolute top-2 right-2 bg-black/70 text-white px-2 py-1 rounded-md backdrop-blur-sm">
                    <div className="flex items-center gap-1">
                      <Camera size={12} />
                      <span className="text-xs font-medium">
                        Müşteri Fotoğrafı
                      </span>
                    </div>
                  </div>
                )}
              </SwiperSlide>
            ))}
            <div className="absolute z-50 top-1/2 -translate-y-1/2 left-0 w-full items-center justify-between px-3 pointer-events-none hidden xl:flex">
              <SwiperButton position="left" />
              <SwiperButton position="right" />
            </div>
          </Swiper>
          <ImageBadges />
          <ImageOverlays />
        </div>
      </div>
    );
  },
);

MainImageContainer.displayName = "MainImageContainer";

export default MainImageContainer;
